/**
 * Embed Card — inline document preview with lazy loading and fullscreen expand.
 *
 * - Lazy loads embeds via IntersectionObserver when card enters viewport.
 * - Internal PDFs rendered via pdfjs (canvas); external via iframe.
 * - Office / Google Docs via their respective viewer iframes.
 * - Fullscreen expand moves the card to document.body (position: fixed via CSS)
 *   without reloading the embedded content.
 * - Error state with retry button.
 *
 * @package Labead
 */

import { downloadFile, printFile, isExternalUrl } from '../functions.js';

/**
 * Initialize all embed cards on page. Safe to call multiple times.
 */
export default function initEmbedCard() {
	document.querySelectorAll('[data-embed-card]').forEach(card => {
		if (card._ecInit) return;
		card._ecInit = true;

		const data = JSON.parse(card.getAttribute('data-embed-card'));
		const body = card.querySelector('.lb-ec-body');
		const loadEl = body ? body.querySelector('.lb-ec-loading') : null;
		const errEl = body ? body.querySelector('.lb-ec-error') : null;

		// ── Lazy load via IntersectionObserver (skip for list mode) ────
		if (!data.list && body) {
			const observer = new IntersectionObserver(entries => {
				if (entries[0].isIntersecting) {
					observer.disconnect();
					startEmbed(data, body, loadEl, errEl);
				}
			}, { rootMargin: '200px' });
			observer.observe(card);
		}

		// ── Expand / collapse ─────────────────────────────────────────
		// Quando a página tem o viewer universal (lb-doc-viewer, incluído no
		// footer da gestão e no explorer do aluno), o expand abre NELE — mesma
		// experiência da área do aluno. Sem viewer (ex.: wp-admin), cai no
		// expand clássico do próprio card.
		const expandBtn = card.querySelector('[data-ec-expand]');
		if (expandBtn) {
			expandBtn.addEventListener('click', () => {
				if (document.querySelector('.lb-doc-viewer')) {
					window.dispatchEvent(new CustomEvent('lb-doc-view', { detail: viewerDetail(data) }));
					return;
				}
				toggleExpand(card, expandBtn, data);
			});
		}

		// ── Download ──────────────────────────────────────────────────
		card.querySelectorAll('.lbfile-download').forEach(btn => {
			btn.addEventListener('click', e => {
				e.stopPropagation();
				downloadFile(data.url, btn);
			});
		});

		// ── Print ─────────────────────────────────────────────────────
		card.querySelectorAll('.lbfile-print').forEach(btn => {
			btn.addEventListener('click', e => {
				e.stopPropagation();
				const iframe = card.querySelector('.lb-ec-frame');
				printFile(data.url, btn, iframe);
			});
		});

		// ── Retry ─────────────────────────────────────────────────────
		const retryBtn = card.querySelector('[data-ec-retry]');
		if (retryBtn) {
			retryBtn.addEventListener('click', () => {
				errEl.style.display = 'none';
				loadEl.style.display = '';
				const existing = body.querySelector('.lb-ec-frame, .lb-ec-pdf, .lb-ec-image-preview');
				if (existing) existing.remove();
				startEmbed(data, body, loadEl, errEl);
			});
		}
	});
}

/**
 * Traduz os dados de um Embed_Card para o detail do viewer universal
 * (evento lb-doc-view — ver lbDocViewer em parts/front/biblioteca.js).
 */
function viewerDetail(data) {
	const url = data.url || '';
	const ext = (url.split('?')[0].split('#')[0].split('.').pop() || '').toLowerCase();
	const type = (data.type || ext || '').toLowerCase();

	let kind = 'iframe';
	if (isImageType(type)) kind = 'image';
	else if (type === 'pdf' && !isExternalUrl(url)) kind = 'pdf';
	else if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'office'].includes(type)) kind = 'office';
	else if (['odt', 'ods', 'odp', 'csv', 'gdoc', 'gsheet', 'gslides', 'google'].includes(type)) kind = 'gdoc';
	else if (['mp3', 'm4a', 'ogg', 'wav'].includes(ext)) kind = 'audio';
	else if (['mp4', 'webm', 'mov'].includes(ext)) kind = 'video';
	else if (type === 'pdf') kind = 'iframe'; // PDF externo: o pdfjs não alcança, iframe direto.

	return {
		id: 0, // Sem doc_id — o viewer não registra atividade para embeds avulsos.
		title: data.title || url.split('/').pop() || '',
		ext: ext.length <= 5 ? ext : '',
		kind,
		url,
		dl: data.download === false ? url : url + (url.includes('?') ? '&' : '?') + 'dl=1',
		size_h: '',
		turma_name: '',
		icon_svg: '',
	};
}

// ─── Embed loading ──────────────────────────────────────────────────────────

/**
 * Create and inject the appropriate embed element into the card body.
 */
function startEmbed(data, body, loadEl, errEl) {
	const { url, type } = data;
	loadEl.style.display = '';
	errEl.style.display = 'none';

	// Images → centered contain (no stretch).
	if (isImageType(type)) {
		const image = document.createElement('img');
		image.className = 'lb-ec-image-preview';
		image.src = url;
		image.alt = data.title || '';
		image.addEventListener('load', () => {
			loadEl.style.display = 'none';
		});
		image.addEventListener('error', () => {
			loadEl.style.display = 'none';
			errEl.style.display = '';
		});
		body.appendChild(image);
		return;
	}

	// Internal PDF → render via pdfjs (canvas pages).
	if (type === 'pdf' && !isExternalUrl(url)) {
		const container = document.createElement('div');
		container.className = 'lb-ec-pdf';
		body.appendChild(container);
		loadPdf(container, url, loadEl, errEl);
		return;
	}

	// Everything else → iframe.
	const iframe = document.createElement('iframe');
	iframe.className = 'lb-ec-frame';

	switch (type) {
		case 'pdf':
			iframe.src = url + '#toolbar=0&navpanes=0&scrollbar=0';
			break;
		case 'doc': case 'docx': case 'xls': case 'xlsx':
		case 'ppt': case 'pptx': case 'office':
			iframe.src = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
			break;
		case 'odt': case 'ods': case 'odp': case 'gdoc':
		case 'gsheet': case 'gslides': case 'csv': case 'google':
			iframe.src = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
			break;
		default:
			iframe.src = url;
	}

	let loaded = false;
	iframe.addEventListener('load', () => {
		loaded = true;
		loadEl.style.display = 'none';
	});
	iframe.addEventListener('error', () => {
		loaded = true;
		loadEl.style.display = 'none';
		errEl.style.display = '';
	});
	// Timeout fallback.
	setTimeout(() => {
		if (!loaded) {
			loadEl.style.display = 'none';
			errEl.style.display = '';
		}
	}, 20000);

	body.appendChild(iframe);
}

/**
 * Check whether embed type is an image.
 */
function isImageType(type) {
	return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'image'].includes(type);
}

// ─── PDF rendering via pdfjs ────────────────────────────────────────────────

/**
 * Load pdfjs lib (if needed) and render PDF pages into container.
 */
function loadPdf(container, url, loadEl, errEl) {
	if (typeof pdfjsLib !== 'undefined') {
		renderPdfPages(container, url, loadEl, errEl);
		return;
	}

	// Load pdfjs on demand.
	const existing = document.querySelector('script[src*="pdfjs.min.js"]');
	if (existing) {
		// Script tag exists but lib not ready yet — wait for it.
		existing.addEventListener('load', () => {
			ensureWorker();
			renderPdfPages(container, url, loadEl, errEl);
		});
		return;
	}

	const script = document.createElement('script');
	script.src = lb_data.assets_url + 'libs/pdfjs/pdfjs.min.js';
	script.onload = () => {
		ensureWorker();
		renderPdfPages(container, url, loadEl, errEl);
	};
	script.onerror = () => {
		loadEl.style.display = 'none';
		errEl.style.display = '';
	};
	document.head.appendChild(script);
}

function ensureWorker() {
	if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
		pdfjsLib.GlobalWorkerOptions.workerSrc = lb_data.assets_url + 'libs/pdfjs/pdfjs-worker.min.js';
	}
}

/**
 * Render all PDF pages as canvases.
 */
function renderPdfPages(container, url, loadEl, errEl) {
	pdfjsLib.getDocument(url).promise.then(pdf => {
		let firstRendered = false;
		for (let p = 1; p <= pdf.numPages; p++) {
			pdf.getPage(p).then(page => {
				const viewport = page.getViewport({ scale: 1.5 });
				const canvas = document.createElement('canvas');
				canvas.width = viewport.width;
				canvas.height = viewport.height;
				container.appendChild(canvas);
				page.render({ canvasContext: canvas.getContext('2d'), viewport });

				if (!firstRendered) {
					firstRendered = true;
					loadEl.style.display = 'none';
				}
			});
		}
	}).catch(() => {
		loadEl.style.display = 'none';
		errEl.style.display = '';
	});
}

// ─── Expand / collapse ──────────────────────────────────────────────────────

/**
 * Toggle fullscreen expand on a card.
 *
 * Moves the card to document.body for clean z-index stacking,
 * adds .lb-ec-expanded (CSS handles fullscreen via position:fixed).
 * The embed content stays in the DOM — no reload.
 *
 * In list mode: creates the body + embed on first expand, destroys on collapse.
 */
function toggleExpand(card, expandBtn, data) {
	const isExpanded = card.classList.contains('lb-ec-expanded');
	const isList = data && data.list;

	if (isExpanded) {
		// Collapse — move card back to its original position.
		card.classList.remove('lb-ec-expanded');
		document.body.style.overflow = '';
		document.removeEventListener('keydown', card._ecKey);

		if (card._ecPlaceholder) {
			card._ecPlaceholder.replaceWith(card);
			card._ecPlaceholder = null;
		}

		card.style.removeProperty('--lb-ec-admin-offset');

		// List mode: destroy the body to free resources.
		if (isList) {
			const body = card.querySelector('.lb-ec-body');
			if (body) body.remove();
		}

		expandBtn.setAttribute('data-lb-dica', expandBtn.dataset.tipExpand);
		expandBtn.setAttribute('aria-label', expandBtn.dataset.tipExpand);
	} else {
		// Save position with a placeholder comment node.
		const ph = document.createComment('lb-ec-ph');
		card.parentNode.insertBefore(ph, card);
		card._ecPlaceholder = ph;

		// List mode: create the body element on the fly.
		if (isList) {
			const body = document.createElement('div');
			body.className = 'lb-ec-body';

			const loadEl = document.createElement('div');
			loadEl.className = 'lb-ec-loading';
			loadEl.innerHTML = '<i data-lucide="loader-2" class="lw:w-6 lw:h-6 lw:animate-spin"></i>';

			const errEl = document.createElement('div');
			errEl.className = 'lb-ec-error';
			errEl.style.display = 'none';
			errEl.innerHTML = '<i data-lucide="alert-triangle" class="lw:w-6 lw:h-6"></i>' +
				'<p>' + (lb_data.strings.embed_error || 'Não foi possível carregar o documento.') + '</p>' +
				'<button type="button" class="lb-ec-retry" data-ec-retry>' +
				'<i data-lucide="refresh-cw" class="lw:w-3.5 lw:h-3.5"></i> ' +
				(lb_data.strings.retry || 'Tentar novamente') + '</button>';

			body.appendChild(loadEl);
			body.appendChild(errEl);
			card.appendChild(body);

			// Retry handler.
			errEl.querySelector('[data-ec-retry]')?.addEventListener('click', () => {
				errEl.style.display = 'none';
				loadEl.style.display = '';
				const existing = body.querySelector('.lb-ec-frame, .lb-ec-pdf, .lb-ec-image-preview');
				if (existing) existing.remove();
				startEmbed(data, body, loadEl, errEl);
			});

			startEmbed(data, body, loadEl, errEl);
		}

		// Move to body for clean stacking context.
		document.body.appendChild(card);

		const adminBar = document.getElementById('wpadminbar');
		const adminOffset = adminBar ? Math.max(0, Math.round(adminBar.getBoundingClientRect().height)) : 0;
		card.style.setProperty('--lb-ec-admin-offset', `${adminOffset}px`);

		// Expand.
		card.classList.add('lb-ec-expanded');
		document.body.style.overflow = 'hidden';

		// Escape key to close.
		card._ecKey = e => { if (e.key === 'Escape') toggleExpand(card, expandBtn, data); };
		document.addEventListener('keydown', card._ecKey);

		expandBtn.setAttribute('data-lb-dica', expandBtn.dataset.tipClose);
		expandBtn.setAttribute('aria-label', expandBtn.dataset.tipClose);
	}
}
